import { FormProvider, getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { AlertDialog, Button, Flex, Strong, Text } from '@radix-ui/themes'
import { useFetcher, useOutletContext } from '@remix-run/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { RxTrash } from 'react-icons/rx'
import { useHydrated } from 'remix-utils/use-hydrated'
import { AddressList } from '~/components/common/address-list.tsx'
import {
	FormDefaults,
	FormDefaultsHiddenField,
} from '~/components/common/form-defaults.tsx'
import { SubmissionButton } from '~/components/common/submission-button.tsx'
import { DropdownDialogItem } from '~/components/ui/dropdown-dialog-item.tsx'
import { type GetAddress } from '~/models/ecommerce-providers/sylius/shop/fetch/address/get-address.ts'
import {
	PROFILE_ADDRESSES_FORM_REMOVE_ADDRESS,
	PROFILE_ADDRESSES_INPUTS,
	profileAddressesRemoveAddressSchema,
} from '~/routes/($lang).profile.addresses/addresses.component.tsx'
import { PROFILE_ADDRESSES_INDEX_I18NEXT } from '~/routes/($lang).profile.addresses._index/index.component.tsx'
import { useProfileAddressesRemoveNotify } from '~/routes/($lang).profile.addresses._index/use-profile-addresses-remove-notify.tsx'
import { type Context, ROOT_INPUTS } from '~/routes/_layout/root.component.tsx'

type DeleteAddressItemProps = GetAddress & {
	setOpen: any
}

const RemoveAddressItem: React.FC<DeleteAddressItemProps> = (props) => {
	const { id, firstName, lastName, setOpen } = props

	const { locale, setApp } = useOutletContext<Context>()
	const { t } = useTranslation(
		PROFILE_ADDRESSES_INDEX_I18NEXT.routeProfileAddressesIndex,
	)
	const isHydrated = useHydrated()
	const fetcher = useFetcher({
		key: PROFILE_ADDRESSES_FORM_REMOVE_ADDRESS.generateFetcherKey!({
			id: String(id),
		}),
	})
  const formValues = {
    [ROOT_INPUTS.subaction]: PROFILE_ADDRESSES_FORM_REMOVE_ADDRESS.subaction,
    [ROOT_INPUTS.noJs]: String(!isHydrated),
  }
	const [form, fields] = useForm({
		defaultValue: {
			...formValues,
			[PROFILE_ADDRESSES_INPUTS.removeAddress]: id,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, {
				schema: profileAddressesRemoveAddressSchema,
			})
		},
		shouldValidate: 'onSubmit',
	})

	useProfileAddressesRemoveNotify({
		setApp,
		fetcher,
		t,
		firstName: firstName!,
		lastName: lastName!,
	})

	return (
		<DropdownDialogItem
			color="red"
			className="flex items-center justify-between gap-x-4"
			triggerChildren={
				<>
					{t('items.button.removeAddress')}
					<RxTrash />
				</>
			}
		>
			<>
				<Flex justify="center" mt="3">
					<Text
						color="gray"
						align={{
							initial: 'center',
							md: 'left',
						}}
					>
						<Flex direction="column" align="center" gapY="5">
							<AlertDialog.Title>
								<Strong>{t('items.common.delete')}</Strong>
							</AlertDialog.Title>
							<AddressList address={props} />
						</Flex>
					</Text>
				</Flex>
				<Flex
					direction={{ initial: 'column', xs: 'row' }}
					gap="3"
					mt="5"
					justify="end"
					align="center"
				>
					<AlertDialog.Action>
						<FormProvider context={form.context}>
							<fetcher.Form
								method="post"
								action={`/${locale}${PROFILE_ADDRESSES_FORM_REMOVE_ADDRESS.action}${location.search}`}
								{...getFormProps(form)}
								onSubmit={() => setOpen(false)}
								preventScrollReset
							>
								<FormDefaults
                  form={form}
                  fields={fields}
                  values={formValues}
                />
								<FormDefaultsHiddenField
									meta={fields[PROFILE_ADDRESSES_INPUTS.removeAddress]}
								/>
								<Button size="3" color="red" type="submit" variant="soft">
									<SubmissionButton fetcher={fetcher}>
										<RxTrash size="1.2em" />
										{t('items.button.removeAddressSubmit')}
									</SubmissionButton>
								</Button>
							</fetcher.Form>
						</FormProvider>
					</AlertDialog.Action>
					<AlertDialog.Cancel>
						<Button size="3" color="gray" variant="surface">
							{t('items.button.cancel')}
						</Button>
					</AlertDialog.Cancel>
				</Flex>
			</>
		</DropdownDialogItem>
	)
}
RemoveAddressItem.displayName = 'RemoveAddressItem'

export { RemoveAddressItem }
